import '.h{display:flex;}.ssr-plugin'
import '.io{max-height:17rem;}.ssr-plugin'
import '.a5{flex-direction:row;}.ssr-plugin'
import '.q{gap:1rem;}.ssr-plugin'
import '.a6{position:absolute;}.ssr-plugin'
import '.ip{left:-9999px;}.ssr-plugin'
import '.cr{opacity:0;}.ssr-plugin'
import '.iq{pointer-events:none;}.ssr-plugin'
import '.bh{height:100%;}.ssr-plugin'
import '.y{width:100%;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.j{align-items:center;}.ssr-plugin'
import '.l{justify-content:center;}.ssr-plugin'
import '.e4{border-radius:0.625rem;}.ssr-plugin'
import '.e2{aspect-ratio:1/1;}.ssr-plugin'
import '.ir{color:#fff;}.ssr-plugin'
import '.b0{font-family:FunnelDisplay;}.ssr-plugin'
import '.fd{font-weight:700;}.ssr-plugin'
import '.b2{font-style:normal;}.ssr-plugin'
import '.is{font-size:3rem;}.ssr-plugin'
import '.it{line-height:100%;}.ssr-plugin'
import '.b1{font-weight:400;}.ssr-plugin'
import '.bu{font-size:0.875rem;}.ssr-plugin'
import '.fl{margin-top:0.5rem;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import Row from '@app/components/layout/Row'
import { useState, useCallback } from 'react'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import { ImageDisplay } from './blocks/ImageDisplay'
import { ImageOveriew } from './blocks/ImageOverview'
import { ImagePreview } from './blocks/ImagePreview'

export type PostMediaGalleryProps = {
  images: string[]
  overviewRenderContainer?: HTMLElement
}

export const PostMediaGallery = ({
  images,
  overviewRenderContainer,
}: PostMediaGalleryProps) => {
  // Always call hooks first
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.mediaGallery
  const [displayedIndex, setDisplayedIndex] = useState<number>(null)
  const [overviewShown, setOverviewShown] = useState(false)

  const renderImagePreview = useCallback(
    (index: number, wrapperStyle?: Style) => {
      return (
        <ImagePreview
          image={images[index]}
          index={index}
          wrapperStyle={{
            minWidth: 0,
            ...wrapperStyle,
          }}
          imageStyle={{
            borderRadius: '0.625rem',
            maxWidth: '100%',
          }}
          onImageClick={setDisplayedIndex}
        />
      )
    },
    [images],
  )

  const modalHeader = useCallback((onClose: () => void) => (
    <Row
      style={{
        flexDirection: 'row-reverse',
        alignItems: 'center',
      }}
    >
      <CloseButton onClick={onClose} />
    </Row>
  ), [])

  const renderOveriew = useCallback(() => {
    return (
      <ImageOveriew
        images={images}
        header={modalHeader(() => setOverviewShown(false))}
        onImageClick={setDisplayedIndex}
      />
    )
  }, [images, modalHeader])

  const goToOverview = useCallback(() => {
    setOverviewShown(true)
    setDisplayedIndex(null)
  }, [])

  const renderDisplay = useCallback(() => {
    return (
      <ImageDisplay
        displayedImage={images[displayedIndex]}
        overviewButtonLabel={translations.allMedia}
        header={modalHeader(() => setDisplayedIndex(null))}
        canGoBack={displayedIndex !== 0}
        canGoForwad={displayedIndex !== images.length - 1}
        goBack={() => setDisplayedIndex((prev) => prev - 1)}
        goForward={() => setDisplayedIndex((prev) => prev + 1)}
        goToOverview={images.length > 1 ? goToOverview : undefined}
      />
    )
  }, [images, displayedIndex, translations.allMedia, modalHeader, goToOverview])

  const renderModal = useCallback(() => {
    if (displayedIndex === null && !overviewShown) {return null}
    return (
      <ModalWrapper
        renderContainer={overviewRenderContainer}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6.5px)',
          overflowY: 'scroll',
          alignItems: displayedIndex !== null ? 'center' : 'flex-start',
        }}
      >
        {displayedIndex !== null ? renderDisplay() : renderOveriew()}
      </ModalWrapper>
    )
  }, [displayedIndex, overviewShown, renderDisplay, renderOveriew, overviewRenderContainer])

  // Early return after all hooks
  if (images.length === 0) {return null}

  if (images.length === 1) {
    return (
      <>
        {renderModal()}
        <div className="h io"
        >
          {renderImagePreview(0)}
        </div>
      </>
    )
  }

  if (images.length < 4) {
    return (
      <>
        {renderModal()}
        <div className="io h a5 q"
        >
          {renderImagePreview(0, { aspectRatio: '1/1' })}
          {images.length > 1 && renderImagePreview(1, { aspectRatio: '1/1' })}
          {images.length > 2 && renderImagePreview(2, { aspectRatio: '1/1' })}
          {images.length > 2 && (
            <div className="a6 ip cr iq"
              onClick={() => setOverviewShown(true)}
            >
              {translations.allMedia}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {renderModal()}
      <div className="io h a5 q"
      >
        {renderImagePreview(0, { aspectRatio: '1/1' })}
        {renderImagePreview(1, { aspectRatio: '1/1' })}
        {images.slice(2).map((_, index) => renderImagePreview(index + 2, { display: 'none' }))}
        <div className="bh y h i j l e4 e2"
          onClick={() => setOverviewShown(true)}
          style={{
            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${images[2]}) lightgray 50% / cover no-repeat`,
          }}
        >
          <div className="ir b0 fd b2 is it"
          >
            +{images.length - 2}
          </div>
          <div className="ir b0 b1 bu fl"
          >
            {translations.allMedia}
          </div>
        </div>
      </div>
    </>
  )
}
